<style scoped>
    .picker-menu {
        min-height: 338px;
        background: white;
    }
    ::v-deep .flatpickr-calendar::after {
        display: none;
    }
    ::v-deep .flatpickr-day.selected {
        background-color: #6353ef;
        border-color: #6353ef;
    }

    .date-message {
        min-height: 22px;
    }

    .restore-message {
        flex: 1 0 40%;
    }
</style>

<template>
    <v-row justify="center">
        <v-dialog
            v-model="isDialogOpen"
            persistent
            max-width="800px"
            scrollable>
            <v-card>
                <v-card-title class="pt-7 px-10">
                    <span class="text-h4 font-weight-medium">
                        {{ $t('backups.restorePointInTime') }}
                    </span>
                </v-card-title>
                <v-card-text class="overflow-visible">
                    <v-container style="min-height: 200px;">
                        <div class="d-flex">
                            <v-row>
                                <v-col cols="12">
                                    <label class="text-uppercase required">
                                        {{ $t('backups.sourceService') }}
                                    </label>
                                    <div>
                                        <v-select
                                            v-model="selectedSourceId"
                                            :loading="isFetchingPITRSources"
                                            :items="sourceServicesList"
                                            class="std std--dropdown"
                                            :menu-props="selectProps"
                                            height="40"
                                            outlined
                                            item-value="serviceId"
                                            item-text="serviceName"
                                            :no-data-text="noDataText"
                                            required
                                            hide-details />
                                    </div>
                                </v-col>
                                <v-col cols="12">
                                    <label class="text-uppercase required">
                                        {{ $t('backups.targetService') }}
                                    </label>
                                    <div>
                                        <v-select
                                            v-model="selectedServiceId"
                                            :items="targetServicesList"
                                            class="std std--dropdown"
                                            :menu-props="selectProps"
                                            height="40"
                                            outlined
                                            item-value="serviceId"
                                            item-text="serviceName"
                                            required
                                            hide-details
                                            :disabled="!selectedSourceId?.length" />
                                    </div>
                                </v-col>
                                <v-col cols="12">
                                    <label class="text-uppercase required">
                                        {{ $t('backups.specifyDateTime') }}
                                    </label>
                                    <v-menu
                                        v-model="dateMenu"
                                        :close-on-content-click="false"
                                        transition="scale-transition"
                                        top
                                        offset-y
                                        min-width="auto"
                                        content-class="picker-menu">
                                        <template #activator="{ on, attrs }">
                                            <v-text-field
                                                v-model="pointInTime"
                                                append-icon="mdi-calendar"
                                                readonly
                                                outlined
                                                dense
                                                min-height="32px"
                                                hide-details
                                                required
                                                placeholder="YYYY-MM-DD HH:MM:SS"
                                                v-bind="attrs"
                                                :disabled="!selectedSourceId?.length"
                                                v-on="on" />
                                        </template>
                                        <flat-pickr
                                            v-if="selectedSourceId?.length"
                                            v-model="pointInTime"
                                            :config="config"
                                            class="flatpickr hide" />
                                    </v-menu>
                                </v-col>
                            </v-row>
                            <div class="ml-10 pt-4 restore-message">
                                <template v-if="!isFetchingPITRSources">
                                    <p>{{ $t('backups.pitrConditionMessage') }}</p>
                                    <a
                                        rel="noopener noreferrer"
                                        class="text-decoration-underline"
                                        @click="$router.push('/backups/service-schedules')">
                                        {{ $t('backups.createNewSchedule') }}
                                    </a>
                                </template>
                            </div>
                        </div>
                        <div class="mt-1 text--primary date-message">
                            {{ minDate?.length && maxDate?.length ?
                                $t(
                                    'backups.dateHint',
                                    [$moment(minDate).format('MMM DD, YYYY, LTS'), $moment(maxDate).format('MMM DD, YYYY, LTS')]
                                ) :
                                ''
                            }}
                        </div>
                    </v-container>
                </v-card-text>
                <v-divider />
                <v-card-actions class="px-9 py-4">
                    <a
                        target="_blank"
                        :href="`${$config.DOCS_URL}Backup%20and%20Restore/Point-in-Time%20Restore/`">
                        {{ $t('learn-more') }}
                    </a>
                    <v-spacer />
                    <v-btn
                        class="text-none"
                        color="primary"
                        rounded
                        outlined
                        :ripple="false"
                        @click="onClose">
                        {{ $t('cancel') }}
                    </v-btn>
                    <v-btn
                        class="text-none"
                        color="primary"
                        style="min-width: 100px;"
                        rounded
                        depressed
                        :ripple="false"
                        :disabled="!isValidPITR || selectedServiceId?.length < 1"
                        :loading="isLoading"
                        @click="isConfirmDialogOpen = true">
                        {{ $t('restore') }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <v-dialog
            v-model="isConfirmDialogOpen"
            persistent
            max-width="600px"
            scrollable>
            <v-card>
                <v-card-title class="pt-9 pb-5 px-9">
                    <span class="text-h4 font-weight-medium">
                        {{ $t('confirmation') }}</span>
                </v-card-title>
                <v-card-text class="pb-5 px-9">
                    <span
                        class="text-body-1 red--text">
                        {{ $t('warn.createRestoreWarning') }}</span>
                </v-card-text>
                <v-divider />
                <v-card-actions class="py-4 px-9">
                    <v-spacer />
                    <v-btn
                        class="text-none"
                        color="primary"
                        rounded
                        outlined
                        :ripple="false"
                        @click="isConfirmDialogOpen = false">
                        {{ $t('cancel') }}
                    </v-btn>
                    <v-btn
                        class="text-none"
                        color="primary"
                        rounded
                        depressed
                        :ripple="false"
                        @click="onSubmit">
                        {{ $t('confirm') }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-row>
</template>

<script>
    import to from 'await-to-js'
    import { mapGetters, mapActions } from 'vuex'
    import { getRestoreTargets } from 'services/backups.js'
    import flatPickr from 'vue-flatpickr-component'
    import 'flatpickr/dist/flatpickr.css'

    export default {
        name: 'PointInTimeModal',
        components: { flatPickr, },
        props: {
            showDialog: {
                type: Boolean,
                required: true,
            },
            isLoading: {
                type: Boolean,
            },
        },
        data() {
            return {
                isDialogOpen: false,
                isConfirmDialogOpen: false,
                sourceServicesList: [],
                selectedSourceId: '',
                targetServicesList: [],
                selectedServiceId: '',
                storage: 'managed',
                pointInTime: '',
                backup: {},
                timeMenu: false,
                dateMenu: false,
                picker: null,
                selectProps: { bottom: true, offsetY: true, contentClass: 'std--dropdown-list', maxHeight: '200px', },
            }
        },
        computed: {
            ...mapGetters(['pitrSources', 'isFetchingPITRSources']),
            noDataText() {
                return this.isFetchingPITRSources ?
                    this.$t('backups.fetchingSourceServices') : this.$t('backups.noSourceServiceFound')
            },
            minDate() {
                return this.backup?.startTime?.length ? this.$help.normalizeDate(this.backup?.startTime) : ''
            },
            maxDate() {
                return this.backup?.endTime?.length ? this.$help.normalizeDate(this.backup?.endTime) : ''
            },
            isValidPITR() {
                if (!this.pointInTime?.length) return false
                const setDateTime = this.$moment(this.$help.normalizeDate(this.pointInTime))
                return setDateTime.isValid() && setDateTime.isBetween(
                    this.minDate,
                    this.maxDate,
                    'second',
                    '[]'
                )
            },
            config() {
                return {
                    inline: true,
                    enableTime: true,
                    dateFormat: 'Y-m-d H:i',
                    enableSeconds: true,
                    dateFormat: 'Y-m-d H:i:S',
                    minuteIncrement: 1,
                    secondIncrement: 1,
                    minDate: this.minDate,
                    maxDate: this.maxDate,
                    onChange: this.handleChange,
                }
            },
        },
        watch: {
            showDialog() {
                this.isDialogOpen = this.showDialog
                if (this.isDialogOpen) {
                    this.loadData()
                }
            },
            pitrSources() {
                this.sourceServicesList = this.pitrSources
            },
            selectedSourceId() {
                if (!this.selectedSourceId?.length) {
                    return
                } else {
                    this.backup = this.sourceServicesList.find(backup => backup.serviceId === this.selectedSourceId) || {}
                    this.getRestoreServices()
                }
            },
        },
        methods: {
            ...mapActions(['fetchPITRSources']),
            async getRestoreServices() {
                try {
                    const [error, services] = await to(getRestoreTargets({ serviceId: this.backup?.serviceId, }))
                    if (error) {
                        this.$store.commit('showMessage', { text: this.$t('errors.fetchServices'), type: this.$config.messageType.ERROR, })
                    } else {
                        if (this.$typy(services).isArray) {
                            this.targetServicesList = services
                        }
                    }
                } catch (error) {
                    this.$store.commit('showMessage', { text: this.$t('errors.fetchServices'), type: this.$config.messageType.ERROR, })
                }
            },
            onClose() {
                this.$emit('on-close')
                this.sourceServicesList = []
                this.selectedSourceId = ''
                this.targetServicesList = []
                this.selectedServiceId = ''
                this.storage = 'managed'
                this.pointInTime = ''
                this.backup = {}
            },
            onSubmit() {
                this.isConfirmDialogOpen = false
                const obj = {
                    serviceId: this.selectedServiceId,
                    id: this.selectedSourceId,
                    ...(this.pointInTime !== '' && {pointInTime: this.pointInTime,}),
                }
                this.$emit('on-restore-submit', obj)
            },
            loadData() {
                this.fetchPITRSources()
            },
            handleChange(selectedDates, dateStr) {
                this.pointInTime = dateStr
            },
        },
    }
</script>
